import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, Select, Input, Typography, Statistic, Row, Col, Tag, Progress, Segmented, Space, Table, theme, EmptyState } from '../../ui';
import { Search } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { request } from '../../api/client';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { fmtQty, fmtPackQty } from '../../lib/format';
import { brand } from '../../assets/brand';

interface BatchRow { batchNo: string; mfgDate: string | null; expDate: string | null; qty: number; unitCost: number; costValue: number; }
interface ProductRow {
  productId: string; product: string; sku: string | null; packSize?: number; uom?: string | null;
  qty: number; batchCount: number; warehouseCount: number; costValue: number; retailValue: number; avgUnitCost: number; marginPct: number; sharePct: number;
}
interface ItemRow {
  warehouseId: string; warehouse: string; productId: string; product: string; sku: string | null;
  packSize?: number; uom?: string | null; mrp: number; qty: number; batchCount: number; costValue: number; retailValue: number;
  value: number; avgUnitCost: number; marginPct: number; sharePct: number; batches: BatchRow[];
}
interface WhRow { warehouseId: string; warehouse: string; qty: number; productCount: number; costValue: number; retailValue: number; sharePct: number; }
interface Summary {
  totalCostValue: number; totalRetailValue: number; totalMargin: number; marginPct: number; totalUnits: number;
  distinctProducts: number; distinctBatches: number; distinctWarehouses: number;
  topProduct: { product: string; costValue: number; sharePct: number } | null;
}
interface ValuationResp { items: ItemRow[]; byProduct: ProductRow[]; byWarehouse: WhRow[]; summary: Summary; totalValue: number; }

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const d10 = (v: string | null) => (v ? String(v).slice(0, 10) : '—');
const marginColor = (m: number) => (m >= 50 ? '#389e0d' : m >= 20 ? '#d48806' : '#cf1322');
const PIE = [brand.primary, brand.secondary, brand.harvest, brand.primaryLight, brand.earth, brand.primaryDark];

export function StockValuationPage({ embedded, summaryOnly }: { embedded?: boolean; summaryOnly?: boolean } = {}) {
  const { token } = theme.useToken();
  const [warehouseId, setWarehouseId] = useState<string>();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'product' | 'detail'>('product');

  const { data: warehouses } = useQuery({ queryKey: ['wh-list'], queryFn: () => request<any[]>({ url: '/warehouses' }) });

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (warehouseId) p.warehouseId = warehouseId;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [warehouseId, search]);

  const { data, isFetching } = useQuery({
    queryKey: ['stock-valuation', params],
    queryFn: () => request<ValuationResp>({ url: '/stock/valuation', params }),
    placeholderData: keepPreviousData,
  });

  const s = data?.summary;
  const byProduct = data?.byProduct ?? [];
  const items = data?.items ?? [];
  const byWarehouse = data?.byWarehouse ?? [];
  const empty = !isFetching && (s?.totalCostValue ?? 0) === 0;
  // The "by warehouse" donut is meaningless with a single warehouse (always 100%) — e.g. a Warehouse
  // Manager scoped to one. In that case show the value split by product instead.
  const showWarehouseDonut = byWarehouse.length > 1;
  // Batch numbers per product (gathered across warehouses) so each row can list them inline.
  const batchNosByProduct = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const it of items) {
      const arr = m.get(it.productId) ?? [];
      for (const b of it.batches ?? []) if (b.batchNo && !arr.includes(b.batchNo)) arr.push(b.batchNo);
      m.set(it.productId, arr);
    }
    return m;
  }, [items]);

  const topProducts = byProduct.slice(0, 10);
  // Shared height so the two breakdown cards always line up; grows with the bar count.
  const chartHeight = Math.max(190, topProducts.length * 30);
  const productBarOption = useMemo(() => ({
    grid: { left: 8, right: 64, top: 10, bottom: 6, containLabel: true },
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      formatter: (ps: any) => {
        const p = ps[0]; const row = topProducts.find((t) => t.product === p.name);
        return `${p.name}<br/><b>${inr(p.value)}</b>${row ? ` · ${row.sharePct}% of total` : ''}`;
      },
    },
    xAxis: { type: 'value', axisLabel: { formatter: (v: number) => (v >= 1000 ? `₹${Math.round(v / 1000)}k` : `₹${v}`) } },
    yAxis: { type: 'category', data: [...topProducts].reverse().map((p) => p.product), axisLabel: { width: 130, overflow: 'truncate' } },
    series: [{
      type: 'bar', data: [...topProducts].reverse().map((p) => p.costValue), barWidth: '55%', barMaxWidth: 26,
      itemStyle: { color: brand.primary, borderRadius: [0, 4, 4, 0] },
      label: { show: true, position: 'right', formatter: (d: any) => inr(d.value), fontSize: 11, color: token.colorTextSecondary },
    }],
  }), [topProducts, token]);

  const whDonutOption = useMemo(() => ({
    tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}<br/><b>${inr(p.value)}</b> · ${p.percent}%` },
    legend: { bottom: 0, type: 'scroll' },
    color: PIE,
    series: [{
      type: 'pie', radius: ['45%', '72%'], center: ['50%', '44%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      label: { formatter: '{d}%', fontSize: 11 },
      data: byWarehouse.map((w) => ({ name: w.warehouse, value: w.costValue })),
    }],
  }), [byWarehouse]);

  // Product split donut — used when there's only one warehouse so the right card stays useful.
  const productDonutOption = useMemo(() => ({
    tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}<br/><b>${inr(p.value)}</b> · ${p.percent}%` },
    legend: { bottom: 0, type: 'scroll' },
    color: PIE,
    series: [{
      type: 'pie', radius: ['45%', '72%'], center: ['50%', '44%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      label: { formatter: '{d}%', fontSize: 11 },
      data: topProducts.map((p) => ({ name: p.product, value: p.costValue })),
    }],
  }), [topProducts]);

  const shareCell = (v: number) => (
    <div style={{ minWidth: 96 }}>
      <Progress percent={v} size="small" strokeColor={brand.primary} format={(p) => `${p}%`} />
    </div>
  );

  // Inline batch-number chips shown under a product/variant in the table row.
  const batchChips = (nos: string[]) => (nos.length ? (
    <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {nos.map((b) => <Tag key={b} style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, lineHeight: '18px' }}>{b}</Tag>)}
    </div>
  ) : null);

  const productCols: DataTableColumn<ProductRow>[] = [
    {
      key: 'product', title: 'Product', dataIndex: 'product', alwaysVisible: true,
      render: (v, r) => (<div><div style={{ fontWeight: 500 }}>{v}</div>{r.sku && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.sku}</div>}{batchChips(batchNosByProduct.get(r.productId) ?? [])}</div>),
    },
    { key: 'qty', title: 'On hand', dataIndex: 'qty', align: 'right', sortable: true, render: (v, r) => fmtPackQty(v, r.packSize, r.uom ?? undefined) },
    { key: 'avgUnitCost', title: 'Avg unit price', dataIndex: 'avgUnitCost', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'costValue', title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right', sortable: true, alwaysVisible: true, render: (v) => <b style={{ color: brand.primaryDark }}>{inr2(v)}</b> },
    { key: 'retailValue', title: 'Value (at MRP)', dataIndex: 'retailValue', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'marginPct', title: 'Margin %', dataIndex: 'marginPct', align: 'right', sortable: true, render: (v: number) => <span style={{ color: marginColor(v), fontWeight: 500 }}>{v}%</span> },
    { key: 'sharePct', title: 'Share of value', dataIndex: 'sharePct', align: 'left', width: 150, sortable: true, render: (v: number) => shareCell(v) },
    { key: 'warehouseCount', title: 'WH', dataIndex: 'warehouseCount', align: 'right', render: (v) => <Tag>{v}</Tag> },
    { key: 'batchCount', title: 'Batches', dataIndex: 'batchCount', align: 'right' },
  ];

  const detailCols: DataTableColumn<ItemRow>[] = [
    {
      key: 'product', title: 'Product', dataIndex: 'product', alwaysVisible: true,
      render: (v, r) => (<div><div style={{ fontWeight: 500 }}>{v}</div>{r.sku && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.sku}</div>}{batchChips((r.batches ?? []).map((b) => b.batchNo))}</div>),
    },
    { key: 'warehouse', title: 'Warehouse', dataIndex: 'warehouse', alwaysVisible: true, render: (v) => <Tag tone="blue">{v}</Tag> },
    { key: 'qty', title: 'On hand', dataIndex: 'qty', align: 'right', sortable: true, render: (v, r) => fmtPackQty(v, r.packSize, r.uom ?? undefined) },
    { key: 'avgUnitCost', title: 'Avg unit price', dataIndex: 'avgUnitCost', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'costValue', title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right', sortable: true, alwaysVisible: true, render: (v) => <b style={{ color: brand.primaryDark }}>{inr2(v)}</b> },
    { key: 'retailValue', title: 'Value (at MRP)', dataIndex: 'retailValue', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'marginPct', title: 'Margin %', dataIndex: 'marginPct', align: 'right', sortable: true, render: (v: number) => <span style={{ color: marginColor(v), fontWeight: 500 }}>{v}%</span> },
    { key: 'sharePct', title: 'Share of value', dataIndex: 'sharePct', align: 'left', width: 150, sortable: true, render: (v: number) => shareCell(v) },
    { key: 'batchCount', title: 'Batches', dataIndex: 'batchCount', align: 'right' },
  ];

  const productExpand = {
    rowExpandable: (r: ProductRow) => items.some((it) => it.productId === r.productId),
    expandedRowRender: (r: ProductRow) => (
      <Table<ItemRow>
        size="small" pagination={false} rowKey="warehouseId"
        dataSource={items.filter((it) => it.productId === r.productId)}
        columns={[
          { title: 'Warehouse', dataIndex: 'warehouse', render: (v) => <Tag tone="blue">{v}</Tag> },
          { title: 'On hand', dataIndex: 'qty', align: 'right' as const, render: (v: number, it: ItemRow) => fmtPackQty(v, it.packSize, it.uom ?? undefined) },
          { title: 'Avg unit price', dataIndex: 'avgUnitCost', align: 'right' as const, render: (v: number) => inr2(v) },
          { title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right' as const, render: (v: number) => <b>{inr2(v)}</b> },
          { title: 'Batches', dataIndex: 'batchCount', align: 'right' as const },
        ]}
      />
    ),
  };

  const detailExpand = {
    rowExpandable: (r: ItemRow) => (r.batches?.length ?? 0) > 0,
    expandedRowRender: (r: ItemRow) => (
      <Table<BatchRow>
        size="small" pagination={false} rowKey="batchNo" dataSource={r.batches}
        columns={[
          { title: 'Batch', dataIndex: 'batchNo' },
          { title: 'Mfg', dataIndex: 'mfgDate', render: (v: string | null) => d10(v) },
          { title: 'Exp', dataIndex: 'expDate', render: (v: string | null) => d10(v) },
          { title: 'On hand', dataIndex: 'qty', align: 'right' as const, render: (v: number) => fmtQty(v) },
          { title: 'Unit price', dataIndex: 'unitCost', align: 'right' as const, render: (v: number) => inr2(v) },
          { title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right' as const, render: (v: number) => <b>{inr2(v)}</b> },
        ]}
      />
    ),
  };

  const filterBar = (
    <Space wrap>
      <Input prefix={<Search className="size-4" />} placeholder="Product / SKU" className="w-50" value={search} onChange={(e) => setSearch(e.target.value)} />
      <Select
        showSearch optionFilterProp="label" placeholder="All warehouses" className="w-55"
        value={warehouseId ?? ''} onChange={(v) => setWarehouseId(v || undefined)}
        options={[
          { value: '', label: 'All warehouses' },
          ...(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: `${w.name} (${w.code})`, isMaster: !!w.isMaster })),
        ]}
      />
    </Space>
  );

  return (
    <div>
      {!embedded && !summaryOnly && (
        <>
          <Typography.Title level={4} style={{ marginBottom: 2 }}>Stock Valuation</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            On‑hand quantity × dealer price, with retail value at MRP. Available stock only.
          </Typography.Paragraph>
        </>
      )}

      {/* KPI cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="1 1 190px"><Card><Statistic title="Stock value (at dealer price)" value={s?.totalCostValue ?? 0} formatter={(v) => inr(v)} valueStyle={{ color: brand.primary }} /></Card></Col>
        <Col flex="1 1 190px"><Card><Statistic title="Retail value (at MRP)" value={s?.totalRetailValue ?? 0} formatter={(v) => inr(v)} valueStyle={{ color: brand.secondary }} /></Card></Col>
        <Col flex="1 1 190px"><Card><Statistic title="Potential margin" value={s?.totalMargin ?? 0} formatter={(v) => inr(v)} valueStyle={{ color: '#389e0d' }} suffix={<span style={{ fontSize: 13, color: token.colorTextTertiary }}>{s ? `${s.marginPct}%` : ''}</span>} /></Card></Col>
        <Col flex="1 1 190px"><Card><Statistic title="On‑hand units" value={s?.totalUnits ?? 0} formatter={(v) => fmtQty(v)} valueStyle={{ color: brand.earth }} /></Card></Col>
        <Col flex="1 1 190px">
          <Card>
            <Statistic title="In stock" value={s?.distinctProducts ?? 0} suffix="SKUs" />
            <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 2 }}>{s?.distinctBatches ?? 0} batches · {s?.distinctWarehouses ?? 0} warehouses</div>
          </Card>
        </Col>
      </Row>

      {/* Breakdown charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={15}>
          <Card title="Stock value by product (top 10)">
            {empty ? <EmptyState title="No stock to value" />
              : <ReactECharts option={productBarOption} notMerge style={{ height: chartHeight }} />}
          </Card>
        </Col>
        <Col xs={24} lg={9}>
          <Card title={showWarehouseDonut ? 'Value by warehouse' : 'Value by product'}>
            {empty ? <EmptyState title="No stock to value" />
              : <ReactECharts option={showWarehouseDonut ? whDonutOption : productDonutOption} notMerge style={{ height: chartHeight }} />}
          </Card>
        </Col>
      </Row>

      {/* View toggle — moved out of the table toolbar into a header row above the filters. */}
      {!summaryOnly && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          <Segmented value={view} onChange={(v) => setView(v as any)} options={[{ label: 'By product', value: 'product' }, { label: 'Detailed', value: 'detail' }]} />
        </div>
      )}

      {/* Detailed table — hidden in summary-only (merged) mode; the Live Stock table carries the rows. */}
      {!summaryOnly && (
      <Card>
        {view === 'product' ? (
          <DataTable<ProductRow>
            key="product"
            storageKey="valuation-by-product"
            rowKey="productId"
            loading={isFetching}
            dataSource={byProduct}
            columns={productCols}
            expandable={productExpand}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} products` }}
            toolbarLeft={filterBar}
          />
        ) : (
          <DataTable<ItemRow>
            key="detail"
            storageKey="valuation-detail"
            rowKey={(r) => `${r.warehouseId}-${r.productId}`}
            loading={isFetching}
            dataSource={items}
            columns={detailCols}
            expandable={detailExpand}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} lines` }}
            toolbarLeft={filterBar}
          />
        )}
      </Card>
      )}
    </div>
  );
}
